import { AriaAttributes, forwardRef, ReactElement } from "react";
import { classNames } from "@/utils/classNames";
import { Spinner } from "./LoadingIndicator";
import { Tag, TagColor } from "./Tag";
import { Icon, IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { IconName } from "./iconMapping";
import { Box } from "./Box";
import { isTMessage, TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import {
  DCFocusEventHandler,
  DCMouseEventHandler,
  mouseEventHandler,
  useDispatch,
  useFocusEventHandler,
  useMouseEventHandler,
  useTranslation,
} from "@/stores/RootStoreContext";
import { logWarn } from "@/utils/log";
import { observer } from "mobx-react";
import { copyToClipboard, EventDispatcher } from "@/events/eventsDomain";
import { DropdownMenu, DropdownMenuItems } from "./Dropdown";
import { noop } from "lodash";

export type ButtonProps = {
  type?: "submit" | "button";
  variant: "primary" | "secondary" | "basic";
  color?: "primary" | "error";
  size?: "md" | "sm" | "xs";
  label?: ReactElement | TMessage | string | number;
  name?: string;
  icon?: ReactElement | IconProps["name"] | IconProps;
  arrow?: "left" | "right" | "up" | "down" | "out";
  loading?: boolean;
  disabled?: boolean;
  /** Buttons are full width by default on mobile, which can be overridden here */
  fullWidth?: boolean;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  className?: string;
  tooltip?: TMessage | string;
  tooltipLabel?: TMessage | string;
  splitActions?: DropdownMenuItems;
} & AriaAttributes;

const iconSizeClasses = "text-xl leading-5 h-5";

const renderButtonIcon = ({
  loading,
  icon,
  className,
}: {
  loading?: boolean;
  icon?: ReactElement | IconProps["name"] | IconProps;
  className?: string;
}) =>
  loading || icon ? (
    <span className={classNames(className, iconSizeClasses)}>
      {loading ? <Spinner /> : icon ? renderIcon(icon) : undefined}
    </span>
  ) : null;

const layoutClassNames = (fullWidth: boolean | undefined) =>
  classNames(
    fullWidth
      ? ["flex", "w-full"]
      : fullWidth === false
        ? ["inline-flex", "w-auto"]
        : ["flex", "w-full", "md:inline-flex", "md:w-auto"],
  );

const renderButton = (
  {
    className,
    type,
    variant,
    size = "md",
    label,
    name,
    icon,
    arrow,
    loading,
    fullWidth,
    onClick,
    color = "primary",
    tooltip,
    tooltipLabel,
    splitActions,
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
  t: TFunction,
  dispatch: EventDispatcher,
  splitSide?: "left" | "right",
) => {
  const iconOnly = (icon || arrow) && !label;
  const isSplit = splitActions && splitActions.length > 0;
  tooltip = typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined;
  const clickHandler = mouseEventHandler<HTMLButtonElement>(dispatch, onClick);

  return (
    <button
      ref={ref}
      {...props}
      aria-label={tooltip}
      data-tooltip={tooltip}
      data-tooltip-label={
        typeof tooltipLabel === "string" ? tooltipLabel : tooltipLabel ? t(tooltipLabel) : undefined
      }
      name={name}
      type={type ?? "button"}
      className={classNames(
        className,
        "whitespace-nowrap",
        "items-center",
        "justify-center",
        "gap-2",
        "min-w-fit",
        "leading-tight",

        "outline-hidden",
        "focus-visible:ring-2",
        "ring-primary/50",

        size === "xs" && (iconOnly ? ["px-1", "py-1", "text-xs"] : ["px-3", "py-1", "text-xs"]),
        size === "sm" && (iconOnly ? ["px-2", "py-2", "text-sm"] : ["px-4", "py-2", "text-sm"]),
        size === "md" && (iconOnly ? ["px-3", "py-3", "text-base"] : ["px-4", "py-3", "text-base"]),

        layoutClassNames(fullWidth),

        isSplit
          ? splitSide === "left"
            ? "rounded-l border-r-0"
            : classNames(
                "rounded-r border-l",
                (variant === "primary" || variant === "secondary") && color === "primary"
                  ? "border-l-primary-light"
                  : "border-l-accent2-light",
                variant === "basic" && "border-l-gray-soft",
              )
          : "rounded-sm",

        variant === "primary" && [
          color === "primary"
            ? "bg-primary hover:bg-primary-dark color-primary-contrast"
            : color === "error"
              ? "bg-accent2 color-accent2-contrast"
              : undefined,
          "font-semibold",
          "border",
          "border-transparent",
          "disabled:bg-gray-hard-dimmed10",
          "disabled:subtle",
        ],

        variant === "secondary" && [
          "border",
          "bg-primary-contrast",
          "font-semibold",
          color === "primary"
            ? [
                "color-primary",
                "border-primary",
                "hover:border-primary-dark",
                "hover:color-primary-dark",
              ]
            : color === "error"
              ? "color-error border-error"
              : undefined,
          "disabled:bg-gray-hard-dimmed10",
          "disabled:border-transparent",
          "disabled:subtle",
          "disabled:hover:subtle",
        ],

        variant === "basic" && [
          "border",
          "border-gray",
          "color-default",
          "bg-primary-contrast",
          "hover:border-primary",
          "disabled:bg-gray-hard-dimmed10",
          "disabled:subtle",
          "disabled:border-transparent",
        ],
      )}
      onClick={loading ? undefined : clickHandler}
    >
      {arrow === "left" && (
        <span className={iconSizeClasses}>
          <Icon name={"arrow-left"} weight={"bold"} />
        </span>
      )}
      {renderButtonIcon({ loading, icon })}
      {label ? <span>{isTMessage(label) ? t(label) : label}</span> : undefined}
      {(arrow === "right" || arrow === "up" || arrow === "down" || arrow === "out") && (
        <span className={iconSizeClasses}>
          <Icon
            name={
              arrow === "right"
                ? "arrow-right"
                : arrow === "up"
                  ? "caret-up"
                  : arrow === "down"
                    ? "caret-down"
                    : "arrow-square-out"
            }
            weight={arrow === "out" ? "regular" : "bold"}
          />
        </span>
      )}
    </button>
  );
};

export const Button = observer(
  forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
    if (!props.label && !props.tooltip) {
      logWarn("<Button> should include either a `label` or `tooltip`");
    }
    const { t } = useTranslation();
    const dispatch = useDispatch();

    if (props.splitActions?.length) {
      return (
        <div className={layoutClassNames(props.fullWidth)}>
          {renderButton(props, ref, t, dispatch, "left")}
          <DropdownMenu
            button={({ isOpen, props: dropdownProps }) =>
              renderButton(
                {
                  ...props,
                  ...(dropdownProps as ButtonProps),
                  label: undefined,
                  icon: undefined,
                  arrow: isOpen ? "up" : "down",
                  fullWidth: false,
                },
                noop,
                t,
                dispatch,
                "right",
              )
            }
            items={props.splitActions}
            alignment="end"
          />
        </div>
      );
    }

    return renderButton(props, ref, t, dispatch);
  }),
);

export type LinkButtonProps = {
  label?: string | TMessage;
  type?: "button" | "submit";
  icon?: ReactElement | IconProps["name"] | IconProps;
  size?: "sm" | "xs";
  arrow?: "left" | "right" | "down" | "up" | "out" | false;
  loading?: boolean;
  disabled?: boolean;
  underline?: boolean;
  color?: "inherit" | "primary" | "error";
  weight?: "semibold" | "normal";
  className?: string;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  tooltip?: TMessage | string;
  testId?: string;
  truncate?: boolean;
} & AriaAttributes;

export const LinkButton = observer(
  forwardRef<HTMLButtonElement, LinkButtonProps>(
    (
      {
        label,
        type = "button",
        icon,
        size,
        arrow,
        loading,
        disabled,
        color = "primary",
        className,
        underline,
        weight = "semibold",
        tooltip,
        onClick,
        testId,
        truncate,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      if (!label && !tooltip) {
        logWarn("<LinkButton> should include either a `label` or `tooltip`");
      }
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      tooltip = typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined;
      return (
        <button
          {...props}
          ref={ref}
          aria-label={tooltip}
          data-tooltip={tooltip}
          data-testid={testId}
          type={type ?? "button"}
          disabled={disabled}
          className={classNames(
            className,
            "cursor-pointer",
            weight === "semibold" ? "font-semibold" : "font-normal",
            "items-center",
            "justify-center",
            "inline-flex",
            "w-auto",
            "gap-1",
            !disabled && "hover:underline",
            disabled && "opacity-50",
            underline && "underline",
            color === "inherit"
              ? ["color-inherit", underline !== false && "underline"]
              : color === "primary"
                ? "color-primary"
                : "color-error",
            size === "xs" ? ["text-sm"] : size === "sm" ? ["text-sm"] : ["text-base"],
            truncate && "overflow-hidden",
          )}
          onClick={loading || disabled ? undefined : clickHandler}
        >
          {arrow === "left" && (
            <span className={"text-xl leading-5 h-5"}>
              <Icon name={"caret-left"} weight={"bold"} />
            </span>
          )}
          {(icon || loading) && (
            <span className={"text-xl leading-5 h-5"}>
              {loading ? <Spinner /> : icon ? renderIcon(icon) : undefined}
            </span>
          )}
          {label && (
            <span className={truncate ? "truncate" : undefined}>
              {isTMessage(label) ? t(label) : label}
            </span>
          )}
          {arrow === "right" && (
            <span className={"text-xl leading-5 h-5"}>
              <Icon name={"caret-right"} weight={"bold"} />
            </span>
          )}
          {arrow === "down" && (
            <span className={"text-xl leading-5 h-5"}>
              <Icon name={"caret-down"} weight={"bold"} />
            </span>
          )}
          {arrow === "up" && (
            <span className={"text-xl leading-5 h-5"}>
              <Icon name={"caret-up"} weight={"bold"} />
            </span>
          )}
          {arrow === "out" && (
            <span className={"text-xl leading-5 h-5"}>
              <Icon name={"arrow-square-out"} color={"subtle"} />
            </span>
          )}
        </button>
      );
    },
  ),
);

export type IconButtonProps = {
  icon: ReactElement | IconProps["name"] | IconProps;
  tooltip: string | TMessage;
  type?: "submit" | "button";
  loading?: boolean;
  disabled?: boolean;
  color?: "primary" | "error" | "gray" | "inherit" | "accent3";
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  onFocus?: DCFocusEventHandler<HTMLButtonElement>;
  onBlur?: DCFocusEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  compact?: boolean;
  className?: string;
  tag?: string;
  tagColor?: TagColor;
} & AriaAttributes;

export const IconButton = observer(
  forwardRef<HTMLButtonElement, IconButtonProps>(
    (
      {
        icon,
        tooltip,
        type,
        loading,
        disabled,
        color = "primary",
        compact,
        className,
        tag,
        tagColor = "error",
        onClick,
        onFocus,
        onBlur,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const tooltipText = typeof tooltip === "string" ? tooltip : tooltip ? t(tooltip) : undefined;
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      const focusHandler = useFocusEventHandler<HTMLButtonElement>(onFocus);
      const blurHandler = useFocusEventHandler<HTMLButtonElement>(onBlur);
      return (
        <button
          {...props}
          ref={ref}
          onClick={disabled || loading ? undefined : clickHandler}
          onFocus={focusHandler}
          onBlur={blurHandler}
          aria-label={tooltipText}
          data-tooltip={tooltipText}
          type={type ?? "button"}
          disabled={disabled}
          className={classNames(
            className,
            "inline-flex",
            "items-center",
            "font-semibold",
            "cursor-pointer",
            "disabled:cursor-default",
            "text-xl",
            "rounded-full",
            !compact && "p-3",
            disabled && "opacity-50",
            color === "inherit"
              ? "color-inherit"
              : color === "error"
                ? "color-error"
                : color === "gray"
                  ? "subtle"
                  : color === "accent3"
                    ? "color-accent3"
                    : "color-primary",
            !disabled &&
              (color === "error"
                ? "hover:bg-error"
                : color === "gray"
                  ? ["hover:bg-alt1", "hover:subtle"]
                  : color === "accent3"
                    ? ["hover:bg-accent3-light", "hover:color-accent3"]
                    : color === "primary"
                      ? ["hover:bg-primary-light", "hover:color-primary-dark"]
                      : ["hover:bg-currentcolor-dimmed"]),
          )}
        >
          {loading ? (
            <Spinner />
          ) : tag ? (
            <Box alignItems={"center"}>
              {renderIcon(icon)}
              <Tag compact label={tag} color={tagColor} />
            </Box>
          ) : (
            renderIcon(icon)
          )}
        </button>
      );
    },
  ),
);

export type ToggleButtonProps = {
  testId?: string;
  selected?: boolean;
  icon?: IconName | IconProps;
  iconPlacement?: "start" | "end";
  label?: string | TMessage | TMessageParam;
  tooltip?: string | TMessage;
  loading?: boolean;
  disabled?: boolean;
  onClick?: DCMouseEventHandler<HTMLButtonElement>;
  variant?: "primary" | "basic" | "small";
  disableMinWidth?: boolean;
  grow?: boolean;
  grouped?: boolean;
} & AriaAttributes;

export const ToggleButton = observer(
  forwardRef<HTMLButtonElement, ToggleButtonProps>(
    (
      {
        testId,
        selected = false,
        icon,
        iconPlacement = "start",
        label,
        tooltip,
        loading,
        disabled,
        variant = "primary",
        onClick,
        disableMinWidth,
        grow,
        grouped,
        ...props
      },
      ref,
    ) => {
      const { t } = useTranslation();
      const clickHandler = useMouseEventHandler<HTMLButtonElement>(onClick);
      const labelText = !label || typeof label === "string" ? label : t(label);
      const tooltipText = !tooltip || typeof tooltip === "string" ? tooltip : t(tooltip);
      const iconElement = renderButtonIcon({
        loading,
        icon,
        className: variant === "basic" && selected ? "color-primary" : undefined,
      });
      return (
        <button
          {...props}
          data-testid={testId}
          ref={ref}
          type={"button"}
          aria-label={labelText || tooltipText}
          data-tooltip={tooltipText}
          aria-pressed={selected}
          onClick={disabled || loading ? undefined : clickHandler}
          disabled={disabled}
          className={classNames(
            "flex",
            "flex-row",
            "gap-2",
            "items-center",
            "justify-center",
            "leading-tight",
            grouped
              ? variant === "small"
                ? [
                    "border",
                    "first:rounded-l",
                    "last:rounded-r",
                    !selected && "[&:not(:first-child):not(:hover)]:border-l-transparent",
                  ]
                : variant === "basic"
                  ? [
                      "border",
                      "first:rounded-l",
                      "last:rounded-r",
                      "[&:not(:first-child):not(:hover)]:border-l-transparent",
                    ]
                  : ["border-y", "border-l", "first:rounded-l", "last:rounded-r", "last:border-r"]
              : ["rounded-sm", "border"],
            disabled && "opacity-50",
            grow && "grow",

            variant === "small" && [
              "h-8 text-xs",
              labelText ? "px-4 py-2" : "w-8 px-0 flex-none",
              selected
                ? ["bg-primary-light", "border-primary"]
                : ["bg-gray-x-soft", "border-gray-soft", !disabled && "hover:border-primary"],
            ],

            variant === "primary" && [
              "px-4 py-3 text-base",
              "font-semibold",
              !disableMinWidth && "min-w-60",
              selected
                ? [
                    "bg-primary",
                    "color-primary-contrast",
                    "border-primary",
                    "hover:bg-primary-dark",
                    "hover:border-primary-dark",
                  ]
                : [
                    "bg-primary-contrast",
                    "color-primary",
                    "border-primary",
                    "hover:color-primary-dark",
                    "hover:border-primary-dark",
                  ],
            ],

            variant === "basic" && [
              !labelText ? "px-3" : "px-4",
              "py-3",
              "text-base",
              !disableMinWidth && "min-w-60",
              selected
                ? [
                    "bg-primary-light",
                    "color-default",
                    "font-semibold",
                    "border-gray",
                    "hover:border-gray-hard",
                  ]
                : ["bg-default", "color-default", "border-gray", "hover:border-gray-hard"],
            ],
          )}
        >
          {iconPlacement === "start" && iconElement}
          {labelText && <div>{labelText}</div>}
          {iconPlacement === "end" && iconElement}
        </button>
      );
    },
  ),
);

export const ToggleButtonGroup = ({ buttons }: { buttons: ToggleButtonProps[] }) => (
  <Box gap={false}>
    {buttons.map((props, i) => (
      <ToggleButton {...props} key={i} grouped />
    ))}
  </Box>
);

export function BackButton({ onClick, name }: { onClick: DCMouseEventHandler; name?: string }) {
  return (
    <Button
      fullWidth={false}
      variant={"basic"}
      icon={"caret-left"}
      tooltip={{ key: "back" }}
      onClick={onClick}
      name={name}
    />
  );
}

export const CloseButton = ({
  onClick,
  color,
}: {
  onClick: IconButtonProps["onClick"];
  color?: IconButtonProps["color"];
}) => {
  return <IconButton icon={"x"} color={color} tooltip={{ key: "close" }} onClick={onClick} />;
};

export const CopyToClipboardButton = ({
  text,
  label,
  withoutIcon,
  strikethrough,
  disabled,
}: {
  text: string;
  label?: TMessage | string;
  withoutIcon?: boolean;
  strikethrough?: boolean;
  disabled?: boolean;
}) => {
  const onClick = useMouseEventHandler([copyToClipboard(text)]);
  const { t } = useTranslation();
  if (label) {
    return (
      <button
        className={classNames(
          "flex gap-2 items-center",
          !strikethrough && !disabled && "hover:underline",
          strikethrough && "strikethrough subtle",
        )}
        aria-label={!disabled ? t("general.copy-to-clipboard") : undefined}
        data-tooltip={!disabled ? t("general.copy-to-clipboard") : undefined}
        onClick={onClick}
        disabled={disabled}
      >
        <span>{isTMessage(label) ? t(label) : label}</span>
        {!withoutIcon && !disabled && <Icon name={"copy"} color={"subtle"} />}
      </button>
    );
  }
  return (
    <IconButton
      icon={"copy"}
      tooltip={{ key: "general.copy-to-clipboard" }}
      onClick={onClick}
      disabled={disabled}
    />
  );
};
